import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  Monitor,
  Package,
  RefreshCw,
} from 'lucide-react';
import { KPICard } from '../components/dashboard/KPICard';
import { RAGSummaryChart, RAGSummary } from '../components/dashboard/RAGSummaryChart';
import { RevenueTrendChart, RevenueTrendPoint } from '../components/dashboard/RevenueTrendChart';
import { AlertsPanel, DashboardAlert } from '../components/alerts/AlertsPanel';
import { ActivityFeed, ActivityItem } from '../components/activity/ActivityFeed';
import { Button } from '../components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { hasLocationScopedRole } from '@/lib/locationScope';

interface KPITrend {
  value: number;
  trend: number;
  direction: 'up' | 'down';
}

interface KPIData {
  salesToday: KPITrend;
  machinesOnline: {
    value: number;
    total: number;
  };
  stockHealth: {
    green: number;
    amber: number;
    red: number;
  };
  refillsToday: KPITrend;
}

interface DashboardData {
  kpis: KPIData;
  ragSummary: RAGSummary;
  alerts: DashboardAlert[];
  recentActivity: ActivityItem[];
  revenueTrend: RevenueTrendPoint[];
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function StockHealthIndicator({ green, amber, red }: { green: number; amber: number; red: number }) {
  const total = green + amber + red;
  if (total === 0) return <span className="text-muted-foreground">-</span>;

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 rounded-full bg-green-500" />
        <span className="text-sm font-medium">{green}</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 rounded-full bg-amber-500" />
        <span className="text-sm font-medium">{amber}</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-3 h-3 rounded-full bg-red-500" />
        <span className="text-sm font-medium">{red}</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { roles } = useAuth();
  const hideEstateWidePanels = hasLocationScopedRole(roles);
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await apiFetch('/api/v1/dashboard');
      
      if (response.status === 403) {
        setError('Access denied. You do not have permission to view the dashboard.');
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const dashboardData = await response.json();
      setData(dashboardData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleRefresh = () => {
    setIsLoading(true);
    fetchDashboard();
  };

  return (
    <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold">Dashboard</h1>
            <p className="text-muted-foreground">Overview of your vending operations</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          <KPICard
            label="Sales Today"
            value={data ? formatCurrency(data.kpis.salesToday.value) : '-'}
            icon={DollarSign}
            trend={
              data
                ? {
                    value: data.kpis.salesToday.trend,
                    direction: data.kpis.salesToday.direction,
                  }
                : undefined
            }
            isLoading={isLoading}
          />
          <KPICard
            label="Machines Online"
            value={data ? data.kpis.machinesOnline.value.toString() : '-'}
            icon={Monitor}
            secondaryValue={data ? `/ ${data.kpis.machinesOnline.total}` : undefined}
            isLoading={isLoading}
            onClick={() => navigate('/machines')}
          />
          <KPICard
            label="Stock Health"
            value=""
            icon={Package}
            isLoading={isLoading}
            onClick={() => navigate('/machines')}
          >
            {data && (
              <StockHealthIndicator
                green={data.kpis.stockHealth.green}
                amber={data.kpis.stockHealth.amber}
                red={data.kpis.stockHealth.red}
              />
            )}
          </KPICard>
          <KPICard
            label="Refills Today"
            value={data ? data.kpis.refillsToday.value.toString() : '-'}
            icon={RefreshCw}
            trend={data ? { value: data.kpis.refillsToday.trend, direction: data.kpis.refillsToday.direction } : undefined}
            isLoading={isLoading}
            onClick={() => navigate('/refills?filter=today')}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-6">
          <RAGSummaryChart
            summary={data?.ragSummary ?? { green: 0, amber: 0, red: 0, offline: 0 }}
            isLoading={isLoading}
          />
          <AlertsPanel
            alerts={data?.alerts ?? []}
            isLoading={isLoading}
          />
        </div>

        <div
          className={`grid grid-cols-1 gap-6 ${
            hideEstateWidePanels ? '' : 'lg:grid-cols-2'
          }`}
        >
          <RevenueTrendChart
            data={data?.revenueTrend ?? []}
            isLoading={isLoading}
          />
          {!hideEstateWidePanels && (
            <ActivityFeed
              activities={data?.recentActivity ?? []}
              isLoading={isLoading}
            />
          )}
        </div>
    </div>
  );
}
